import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, X, Languages, Loader2, Send, Star, ShieldCheck } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { useTranslate } from "../context/TranslateContext";
import { useMyCinema } from "../context/MyCinemaContext";
import { LANGUAGES } from "../mock/data";
import { createContent, translateFields } from "../lib/api";

const TYPES = [
  { v: "article", label: "Article / Editorial" },
  { v: "movie", label: "Movie entry" },
  { v: "person", label: "Person profile" },
  { v: "song", label: "Song / Music" },
  { v: "news", label: "News / Update" },
];

const langName = (code) => LANGUAGES.find((l) => l.code === code) || { code, name: code, native: code };
const emptyVersion = () => ({ title: "", summary: "", body: "" });

export default function Contribute() {
  const { t } = useTranslate();
  const { user } = useMyCinema();

  const [type, setType] = useState("article");
  const [tags, setTags] = useState("");
  const [primaryLang, setPrimaryLang] = useState("en");
  const [activeLang, setActiveLang] = useState("en");
  const [langs, setLangs] = useState({ en: emptyVersion() });
  const [authorName, setAuthorName] = useState(user?.name || "");
  const [authorEmail, setAuthorEmail] = useState(user?.email || "");
  const [translating, setTranslating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const addedCodes = Object.keys(langs);
  const available = useMemo(
    () => LANGUAGES.filter((l) => !addedCodes.includes(l.code)),
    [addedCodes]
  );

  const setField = (field, value) =>
    setLangs((s) => ({ ...s, [activeLang]: { ...s[activeLang], [field]: value } }));

  const addLanguage = (code) => {
    if (!code || langs[code]) return;
    setLangs((s) => ({ ...s, [code]: emptyVersion() }));
    setActiveLang(code);
  };

  const removeLanguage = (code) => {
    if (code === primaryLang) { toast.error(t("Set another primary language before removing this one.")); return; }
    setLangs((s) => { const n = { ...s }; delete n[code]; return n; });
    if (activeLang === code) setActiveLang(primaryLang);
  };

  const autoTranslate = async () => {
    if (activeLang === primaryLang) { toast.info(t("Switch to a non-primary language to auto-translate into it.")); return; }
    const src = langs[primaryLang];
    if (!src.title && !src.summary && !src.body) { toast.error(t("Add content in the primary language first.")); return; }
    setTranslating(true);
    try {
      const target = langName(activeLang);
      const out = await translateFields(
        { title: src.title, summary: src.summary, body: src.body },
        target.name, target.code
      );
      setLangs((s) => ({ ...s, [activeLang]: { title: out.title || "", summary: out.summary || "", body: out.body || "" } }));
      toast.success(`${t("Translated into")} ${target.name}`);
    } catch (e) {
      toast.error(t("Auto-translation failed. Please try again."));
    } finally {
      setTranslating(false);
    }
  };

  const submit = async () => {
    const p = langs[primaryLang];
    if (!p.title.trim() || !p.body.trim()) { toast.error(t("The primary language needs at least a title and body.")); return; }
    if (!authorName.trim() || !authorEmail.trim()) { toast.error(t("Please add your name and email.")); return; }
    setSubmitting(true);
    try {
      await createContent({
        type,
        author_email: authorEmail,
        author_name: authorName,
        primary_lang: primaryLang,
        langs,
        tags: tags.split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success(t("Submitted for editorial review. Thank you for contributing!"));
      setLangs({ en: emptyVersion() });
      setPrimaryLang("en"); setActiveLang("en"); setTags("");
    } catch (e) {
      toast.error(t("Submission failed. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const av = langs[activeLang] || emptyVersion();
  const rtl = !!langName(activeLang).rtl;

  const listenText = "Contribute to IndiasCinema. Create rich, multilingual content — write in any Indian or international language, add as many language versions as you like, and let AI help you translate. Your submission goes to our editors for review.";

  return (
    <div className="min-h-screen bg-paper" data-testid="contribute-page">
      <InnerNav />

      <div className="mx-auto max-w-4xl px-6 py-14 md:py-16">
        <div className="flex items-center gap-2 font-body text-xs uppercase tracking-[0.28em] text-gold">
          <Languages className="h-4 w-4" /> {t("Multilingual CMS")}
        </div>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight text-cream sm:text-5xl lg:text-6xl">
          {t("Contribute to ")}<span className="italic text-gradient-gold">IndiasCinema</span>
        </h1>
        <p className="mt-5 max-w-2xl font-body text-lg leading-relaxed text-ash">
          {t("Write in any language, add multiple language versions side by side, and let AI help you translate. Every submission is reviewed by our editors before publishing.")}
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          
          <Link to="/admin/content" data-testid="link-admin"
            className="inline-flex items-center gap-2 rounded-full border border-edge px-4 py-2 font-body text-sm text-ash transition-colors hover:border-gold hover:text-gold">
            <ShieldCheck className="h-4 w-4" /> {t("Editor dashboard")}
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Left: meta */}
          <div className="space-y-6 lg:col-span-1">
            <Field label={t("Content type")}>
              <select data-testid="content-type" value={type} onChange={(e) => setType(e.target.value)}
                className="w-full rounded-xl border border-edge bg-surface px-4 py-3 font-body text-cream focus:border-gold focus:outline-none">
                {TYPES.map((o) => <option key={o.v} value={o.v} className="bg-paper">{t(o.label)}</option>)}
              </select>
            </Field>

            <Field label={t("Tags (comma separated)")}>
              <input data-testid="content-tags" value={tags} onChange={(e) => setTags(e.target.value)}
                placeholder="malayalam, review, 1990s"
                className="w-full rounded-xl border border-edge bg-surface px-4 py-3 font-body text-cream placeholder:text-ash focus:border-gold focus:outline-none" />
            </Field>

            <Field label={t("Your name")}>
              <input data-testid="author-name" value={authorName} onChange={(e) => setAuthorName(e.target.value)}
                className="w-full rounded-xl border border-edge bg-surface px-4 py-3 font-body text-cream placeholder:text-ash focus:border-gold focus:outline-none" />
            </Field>
            <Field label={t("Your email")}>
              <input data-testid="author-email" value={authorEmail} onChange={(e) => setAuthorEmail(e.target.value)}
                className="w-full rounded-xl border border-edge bg-surface px-4 py-3 font-body text-cream placeholder:text-ash focus:border-gold focus:outline-none" />
            </Field>

            <Field label={t("Primary language")}>
              <select data-testid="primary-lang" value={primaryLang}
                onChange={(e) => { const c = e.target.value; addLanguage(c); setPrimaryLang(c); setActiveLang(c); }}
                className="w-full rounded-xl border border-edge bg-surface px-4 py-3 font-body text-cream focus:border-gold focus:outline-none">
                {addedCodes.map((c) => <option key={c} value={c} className="bg-paper">{langName(c).name}</option>)}
              </select>
              <p className="mt-2 font-body text-xs text-ash">{t("The version editors treat as the source of truth.")}</p>
            </Field>
          </div>

          {/* Right: multilingual editor */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-edge bg-surface/40 p-5">
              {/* Language tabs */}
              <div className="flex flex-wrap items-center gap-2" data-testid="lang-tabs">
                {addedCodes.map((c) => {
                  const active = c === activeLang;
                  return (
                    <span key={c} className={`group inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-body text-sm transition-colors ${
                      active ? "border-gold bg-gold/10 text-gold" : "border-edge text-ash hover:border-gold hover:text-gold"
                    }`}>
                      <button data-testid={`lang-tab-${c}`} onClick={() => setActiveLang(c)} className="flex items-center gap-1.5">
                        {c === primaryLang && <Star className="h-3 w-3 fill-gold text-gold" />}
                        {langName(c).native} <span className="text-xs opacity-70">{langName(c).name}</span>
                      </button>
                      {addedCodes.length > 1 && (
                        <button data-testid={`lang-remove-${c}`} onClick={() => removeLanguage(c)} className="opacity-60 hover:opacity-100">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  );
                })}

                {/* Add language */}
                <div className="relative inline-flex items-center gap-1 rounded-full border border-dashed border-gold/50 px-2 py-1">
                  <Plus className="h-3.5 w-3.5 text-gold" />
                  <select data-testid="add-language" value="" onChange={(e) => addLanguage(e.target.value)}
                    className="cursor-pointer bg-transparent py-0.5 pr-1 font-body text-sm text-gold focus:outline-none">
                    <option value="" className="bg-paper text-cream">{t("Add language")}</option>
                    <optgroup label={t("Indian Languages")}>
                      {available.filter((l) => l.indian).map((l) => (
                        <option key={l.code} value={l.code} className="bg-paper text-cream">{l.native} — {l.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label={t("International")}>
                      {available.filter((l) => !l.indian).map((l) => (
                        <option key={l.code} value={l.code} className="bg-paper text-cream">{l.native} — {l.name}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              </div>

              {/* Active language editor */}
              <div className="mt-5 flex items-center justify-between gap-3">
                <div className="font-body text-sm text-ash">
                  {t("Editing")}: <span className="font-medium text-cream">{langName(activeLang).name}</span>
                  {activeLang === primaryLang && <span className="ml-2 rounded-full bg-gold/10 px-2 py-0.5 text-xs text-gold">{t("Primary")}</span>}
                </div>
                {activeLang !== primaryLang && (
                  <button data-testid="auto-translate" onClick={autoTranslate} disabled={translating}
                    className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-4 py-2 font-body text-sm text-gold transition-colors hover:bg-gold/10 disabled:opacity-60">
                    {translating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}
                    {t("Auto-translate from")} {langName(primaryLang).name}
                  </button>
                )}
              </div>

              <div className="mt-4 space-y-4" dir={rtl ? "rtl" : "ltr"}>
                <input data-testid="field-title" value={av.title} onChange={(e) => setField("title", e.target.value)}
                  placeholder={t("Title")}
                  className="w-full rounded-xl border border-edge bg-surface px-4 py-3 font-display text-2xl text-cream placeholder:text-ash focus:border-gold focus:outline-none" />
                <textarea data-testid="field-summary" value={av.summary} onChange={(e) => setField("summary", e.target.value)}
                  placeholder={t("Short summary / standfirst")} rows={2}
                  className="w-full resize-y rounded-xl border border-edge bg-surface px-4 py-3 font-body text-cream placeholder:text-ash focus:border-gold focus:outline-none" />
                <textarea data-testid="field-body" value={av.body} onChange={(e) => setField("body", e.target.value)}
                  placeholder={t("Write the full content here…")} rows={12}
                  className="w-full resize-y rounded-xl border border-edge bg-surface px-4 py-3 font-body text-base leading-relaxed text-cream placeholder:text-ash focus:border-gold focus:outline-none" />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-4">
              <span className="font-body text-sm text-ash">{addedCodes.length} {t("language version(s)")}</span>
              <button data-testid="submit-content" onClick={submit} disabled={submitting}
                className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-body font-medium text-ink transition-transform hover:scale-105 disabled:opacity-60">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {t("Submit for review")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="mb-2 block font-body text-xs uppercase tracking-widest text-gold">{label}</label>
    {children}
  </div>
);
